import { useState, useEffect } from 'react';

// Import JSON files as modules
import en from './en.json';
import es from './es.json';
import ar from './ar.json';
import de from './de.json';
import fr from './fr.json';
import it from './it.json';
import pt from './pt.json';
import zh from './zh.json';
import nl from './nl.json';
import fa from './fa.json';

// Define the available languages
const availableLanguages = {
    en,
    es,
    ar,
    de,
    fr,
    it,
    pt,
    zh,
    nl,
    fa
};

type Language = keyof typeof availableLanguages;

// Base translations type - this represents all possible keys that can exist
export type Translations = {
  "DAYS": string
  "HOURS": string
  "MINUTES": string
  "SECONDS": string
  "STARTING_SOON": string
  "EVENT_NOT_STARTED": string
  "DEFAULT_ERROR": string
  "DEFAULT_ERROR_DESCRIPTION": string
  "API_ERROR": string
  "API_ERROR_DESCRIPTION": string
  "ERROR_CODE_NOT_AUTHENTICATED": string
  "ERROR_CODE_NOT_AUTHENTICATED_DESCRIPTION": string
  "ERROR_CODE_GEO_BLOCKED": string
  "ERROR_CODE_GEO_BLOCKED_DESCRIPTION": string
  "ERROR_CODE_NOT_ENTITLED": string
  "ERROR_CODE_NOT_ENTITLED_DESCRIPTION": string
  "ERROR_CODE_VPN_BLOCKED": string
  "ERROR_CODE_VPN_BLOCKED_DESCRIPTION": string
  "ERROR_CODE_IP_CHANGED": string
  "ERROR_CODE_IP_CHANGED_DESCRIPTION": string
  "ERROR_CODE_VPN_DETECTED": string
  "ERROR_CODE_VPN_DETECTED_DESCRIPTION": string
  "ERROR_CODE_ANONYMOUS_PROXY": string
  "ERROR_CODE_ANONYMOUS_PROXY_DESCRIPTION": string
  "CONCURRENCY_KICKED": string
  "CONCURRENCY_KICKED_DESCRIPTION": string
  "CONCURRENCY_CONNECTION_FAILED": string
  "CONCURRENCY_CONNECTION_FAILED_DESCRIPTION": string
}

// Partial translations type for individual language files (allows missing keys)
type PartialTranslations = Partial<Translations>;

// Function to get the browser's language setting
const getBrowserLanguage = (): Language => {
  const language = navigator.language.split('-')[0] as Language;
  return availableLanguages[language] ? language : 'en';
};

const useMessages = (locale: string) => {
  const [language, setLanguage] = useState<Language>('en');
  const [translations, setTranslations] = useState<PartialTranslations>(availableLanguages.en);

  useEffect(() => {
    const lang = !!availableLanguages?.[locale as Language] ? locale as Language :  getBrowserLanguage();;
    setLanguage(lang);
    setTranslations(availableLanguages[lang] as PartialTranslations);
  }, [locale]);

  // Translation function with English fallback
  const t = (key: keyof Translations): string => {
    // Try current language first
    if (translations[key]) {
      return translations[key];
    }
    
    // Fallback to English if key not found in current language
    if (language !== 'en' && availableLanguages.en[key]) {
      return availableLanguages.en[key];
    }
    
    // Return empty string if not found in either current language or English
    return '';
  };

  // Function to change the language manually
  const changeLanguage = (lng: Language) => {
    if (availableLanguages[lng]) {
      setLanguage(lng);
      setTranslations(availableLanguages[lng] as PartialTranslations);
    }
  };

  return { t, language, changeLanguage };
};

export default useMessages;
